import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, CalendarDays, List, SlidersHorizontal, Sparkles } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sessionProgramsApi } from '../../services/api/session-programs.api';
import { authApi, type AdminAuthUser } from '../../services/api/auth.api';
import { canPerformAdminAction, canSeeAdminSection } from '../../utils/adminPermissions';
import { AdminBuildStamp } from '../../components/admin/AdminBuildStamp';
import { CityPicker } from '../../components/admin/CityPicker';
import { FacilitatorPicker } from '../../components/admin/FacilitatorPicker';
import { ProgramBulkTools, type BulkMode } from '../../components/admin/ProgramBulkTools';
import { ProgramSessionsCalendar, type ProgramSlotDraft } from '../../components/admin/ProgramSessionsCalendar';
import { ProgramSessionsList } from '../../components/admin/ProgramSessionsList';
import { ProgramSlotEditor, ProgramSlotEditorPlaceholder } from '../../components/admin/ProgramSlotEditor';
import { SessionLanguagePicker } from '../../components/admin/SessionLanguagePicker';
import {
  SESSION_GAME_MODE_OPTIONS,
  SESSION_TIMEZONE_OPTIONS,
  formatTimezoneOffset,
  timezoneShortLabel,
} from '../../components/admin/sessionFormOptions';
import type { GameMode, Language, SessionProgram } from '@business-game/shared';

type SessionsView = 'calendar' | 'list';

type SlotDraft = ProgramSlotDraft;

const defaultSlotLabel = (index: number) => `Séance ${index + 1}`;

const formatDateRange = (slots: SlotDraft[], timezone: string) => {
  const dates = slots
    .filter((s) => s.scheduledAt)
    .map((s) => new Date(s.scheduledAt))
    .sort((a, b) => a.getTime() - b.getTime());

  if (dates.length === 0) return null;

  const fmt = (d: Date) =>
    d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: timezone || undefined });

  if (dates.length === 1) return fmt(dates[0]);
  return `du ${fmt(dates[0]).replace(/\s\d{4}$/, '')} au ${fmt(dates[dates.length - 1])}`;
};

export const AdminProgramFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const programId = id ? Number(id) : null;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState<AdminAuthUser | null>(null);
  const [editingProgram, setEditingProgram] = useState<SessionProgram | null>(null);
  const [sessionsView, setSessionsView] = useState<SessionsView>('calendar');
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
  const [bulkMode, setBulkMode] = useState<BulkMode>('none');
  const [multiSelectedDates, setMultiSelectedDates] = useState<string[]>([]);

  const [form, setForm] = useState({
    name: '',
    gameMode: 'teamlab' as GameMode,
    defaultLanguage: 'fr' as Language,
    defaultTime: '09:00',
    defaultTimezone: 'Europe/Paris',
    defaultLocation: '',
    defaultFacilitator: '',
    slots: [] as SlotDraft[],
  });

  useEffect(() => {
    authApi.me().then(setCurrentUser).catch(() => setCurrentUser(null));
  }, []);

  const canViewPrograms = canSeeAdminSection(currentUser, 'programs');
  const canSaveProgram = isEdit
    ? canPerformAdminAction(currentUser, 'programsEdit')
    : canPerformAdminAction(currentUser, 'programsCreate');

  useEffect(() => {
    if (currentUser && !canViewPrograms) {
      navigate('/admin/sessions');
    } else if (currentUser && !canSaveProgram) {
      navigate('/admin/programs');
    }
  }, [currentUser, canViewPrograms, canSaveProgram, navigate]);

  useEffect(() => {
    if (!programId || Number.isNaN(programId)) return;

    const load = async () => {
      setLoading(true);
      try {
        const program = await sessionProgramsApi.getProgramById(programId);
        setEditingProgram(program);
        const firstSlot = program.slots[0];
        setForm({
          name: program.name,
          gameMode: program.gameMode,
          defaultLanguage: program.defaults?.language ?? firstSlot?.language ?? 'fr',
          defaultTime:
            program.defaults?.time ??
            (firstSlot?.scheduledAt ? new Date(firstSlot.scheduledAt).toISOString().slice(11, 16) : '09:00'),
          defaultTimezone: program.defaults?.timezone ?? firstSlot?.timezone ?? 'Europe/Paris',
          defaultLocation: program.defaults?.location ?? '',
          defaultFacilitator: program.defaults?.facilitator ?? '',
          slots:
            program.slots.length > 0
              ? program.slots.map((slot, index) => ({
                  slotId: slot.id,
                  label: slot.label || defaultSlotLabel(index),
                  scheduledAt: new Date(slot.scheduledAt).toISOString().slice(0, 16),
                  language: slot.language || 'fr',
                  timezone: slot.timezone || 'Europe/Paris',
                  location: slot.location ?? '',
                  facilitator: slot.facilitator ?? '',
                }))
              : [],
        });
      } catch (error) {
        console.error('Failed to load program:', error);
        navigate('/admin/programs');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [programId, navigate]);

  const sessionCodes = useMemo(() => {
    const map: Record<number, string | null | undefined> = {};
    editingProgram?.slots.forEach((slot) => {
      if (slot.id && slot.sessionCode) map[slot.id] = slot.sessionCode;
    });
    return map;
  }, [editingProgram]);

  const scheduledCount = useMemo(
    () => form.slots.filter((slot) => slot.scheduledAt).length,
    [form.slots]
  );

  const dateRange = useMemo(
    () => formatDateRange(form.slots, form.defaultTimezone),
    [form.slots, form.defaultTimezone]
  );

  const gameModeLabel = SESSION_GAME_MODE_OPTIONS.find((m) => m.value === form.gameMode)?.label ?? 'Team Lab';

  /** Une séance est une "exception" si elle diffère du modèle (heure, langue, fuseau, lieu ou animateur). */
  const isSlotException = (slot: SlotDraft): boolean => {
    const slotTime = slot.scheduledAt.length >= 16 ? slot.scheduledAt.slice(11, 16) : '';
    return (
      slot.language !== form.defaultLanguage ||
      slot.timezone !== form.defaultTimezone ||
      (slot.location || '') !== (form.defaultLocation || '') ||
      (slot.facilitator || '') !== (form.defaultFacilitator || '') ||
      (slotTime !== '' && slotTime !== form.defaultTime)
    );
  };

  const existingDates = useMemo(
    () => new Set(form.slots.filter((slot) => slot.scheduledAt).map((slot) => slot.scheduledAt.slice(0, 10))),
    [form.slots]
  );

  const addManyDates = (isoDates: string[]) => {
    if (isoDates.length === 0) return;
    const sorted = [...isoDates].sort();
    setForm((current) => {
      const additions: SlotDraft[] = sorted.map((isoDate, offset) => ({
        label: defaultSlotLabel(current.slots.length + offset),
        scheduledAt: `${isoDate}T${current.defaultTime}`,
        language: current.defaultLanguage,
        timezone: current.defaultTimezone,
        location: current.defaultLocation,
        facilitator: current.defaultFacilitator,
      }));
      return { ...current, slots: [...current.slots, ...additions] };
    });
  };

  const toggleMultiDate = (isoDate: string) => {
    setMultiSelectedDates((current) =>
      current.includes(isoDate) ? current.filter((d) => d !== isoDate) : [...current, isoDate]
    );
  };

  const duplicateSlot = (index: number) => {
    const source = form.slots[index];
    const duplicateLabel = source.label.trim()
      ? `${source.label.trim()} (copie)`
      : defaultSlotLabel(form.slots.length);

    const slots = [...form.slots];
    const insertAt = index + 1;
    slots.splice(insertAt, 0, {
      label: duplicateLabel,
      scheduledAt: source.scheduledAt,
      language: source.language,
      timezone: source.timezone,
      location: source.location,
      facilitator: source.facilitator,
    });
    setForm({ ...form, slots });
    setSelectedSlotIndex(insertAt);
  };

  const updateSlot = (index: number, patch: Partial<SlotDraft>) => {
    const slots = [...form.slots];
    slots[index] = { ...slots[index], ...patch };
    setForm({ ...form, slots });
  };

  const removeSlot = (index: number) => {
    setForm({ ...form, slots: form.slots.filter((_, i) => i !== index) });
    setSelectedSlotIndex((current) => {
      if (current === null) return null;
      if (current === index) return null;
      if (current > index) return current - 1;
      return current;
    });
  };

  const addSeance = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isoDate = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    const newIndex = form.slots.length;

    setForm({
      ...form,
      slots: [
        ...form.slots,
        {
          label: defaultSlotLabel(newIndex),
          scheduledAt: `${isoDate}T${form.defaultTime}`,
          language: form.defaultLanguage,
          timezone: form.defaultTimezone,
          location: form.defaultLocation,
          facilitator: form.defaultFacilitator,
        },
      ],
    });
    setSelectedSlotIndex(newIndex);
    setSessionsView('list');
  };

  const selectedSlot = selectedSlotIndex !== null ? form.slots[selectedSlotIndex] : null;
  const selectedSessionCode =
    selectedSlot?.slotId && sessionCodes[selectedSlot.slotId] ? sessionCodes[selectedSlot.slotId] : null;
  const hasSlotSelected = selectedSlotIndex !== null && selectedSlot !== null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);

    const slots = form.slots
      .filter((slot) => slot.scheduledAt)
      .map((slot, index) => ({
        id: slot.slotId,
        label: slot.label.trim() || defaultSlotLabel(index),
        order: index,
        scheduledAt: new Date(slot.scheduledAt),
        language: slot.language,
        timezone: slot.timezone,
        location: slot.location.trim() || null,
        facilitator: slot.facilitator.trim() || null,
      }));

    const defaults = {
      language: form.defaultLanguage,
      time: form.defaultTime,
      timezone: form.defaultTimezone,
      location: form.defaultLocation.trim() || null,
      facilitator: form.defaultFacilitator.trim() || null,
    };

    try {
      if (isEdit && programId) {
        await sessionProgramsApi.updateProgram(programId, {
          name: form.name.trim(),
          gameMode: form.gameMode,
          defaults,
          slots,
        });
      } else {
        await sessionProgramsApi.createProgram({
          name: form.name.trim(),
          gameMode: form.gameMode,
          defaults,
          slots,
        });
      }
      navigate('/admin/programs');
    } catch (error) {
      console.error('Failed to save program:', error);
      alert(
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
          'Erreur lors de la sauvegarde'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-slate-500">Chargement...</div>;
  }

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <div className="sticky top-0 z-10 -mr-6 -mt-6 mb-0 flex items-center justify-between border-b border-indigo-200/60 bg-white/90 px-6 py-4 shadow-sm shadow-indigo-500/5 backdrop-blur-sm lg:-mr-8 lg:-mt-8 lg:px-8">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/programs"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 transition-colors hover:bg-white hover:text-slate-800"
            aria-label="Retour aux programmes"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Programmes <span className="text-slate-300">/</span>{' '}
            <span className="text-indigo-600">{isEdit ? 'Modifier' : 'Nouveau'}</span>
          </span>
        </div>
        <Link
          to="/admin/programs"
          className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
        >
          Annuler
        </Link>
      </div>

      <div className="mx-auto w-full max-w-[1600px] flex-grow px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-indigo-600">
            <Sparkles className="h-3 w-3" />
            Sessions en série
          </span>
          <h1 className="mt-4 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            {isEdit ? 'Modifier le programme' : 'Nouveau programme'}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-500">
            Définissez le modèle une fois, puis ajoutez vos dates en masse. Chaque date devient une session avec son
            propre code — modifiez seulement les exceptions (lieu, animateur, fuseau).
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="relative min-w-0">
            {/* Configuration — absolute sur desktop, repliée en rail fantôme */}
            <aside
              className={[
                'w-full transition-all duration-500 ease-in-out',
                'xl:absolute xl:left-0 xl:top-0 xl:z-[1]',
                hasSlotSelected ? 'xl:w-12 xl:opacity-40' : 'xl:w-[280px] xl:opacity-100',
              ].join(' ')}
            >
              <div
                className={[
                  'relative rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm shadow-indigo-500/5 transition-all duration-500',
                  hasSlotSelected ? 'xl:pointer-events-none xl:scale-[0.92] xl:p-2 xl:blur-[2px]' : '',
                ].join(' ')}
              >
                <div className={hasSlotSelected ? 'xl:invisible xl:absolute xl:inset-0 xl:overflow-hidden' : ''}>
                <h2 className="text-lg font-bold text-slate-900">Modèle</h2>
                <p className="mt-1 text-xs text-slate-400">Valeurs appliquées à chaque nouvelle séance.</p>

                <div className="mt-5">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Nom du programme
                  </label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-xl border-2 border-indigo-200 bg-white px-4 py-3.5 font-semibold text-slate-900 shadow-[0_0_0_4px_rgba(79,70,229,0.08)] focus:border-indigo-500 focus:outline-none"
                    placeholder="Ex: Total Energies M5"
                  />
                </div>

                <div className="mt-5">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Mode de jeu
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {SESSION_GAME_MODE_OPTIONS.map((mode) => {
                      const selected = form.gameMode === mode.value;
                      const isLeadlab = mode.value === 'leadlab';
                      return (
                        <label
                          key={mode.value}
                          className={[
                            'cursor-pointer rounded-xl border-2 px-3 py-3 text-center transition-all',
                            selected
                              ? isLeadlab
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-indigo-500 bg-indigo-50'
                              : 'border-slate-200 bg-white hover:border-slate-300',
                          ].join(' ')}
                        >
                          <input
                            type="radio"
                            name="program_game_mode"
                            value={mode.value}
                            checked={selected}
                            onChange={() => setForm({ ...form, gameMode: mode.value })}
                            className="sr-only"
                          />
                          <span className="block text-sm font-bold text-slate-900">{mode.label}</span>
                          <span className="mt-0.5 block text-[11px] text-slate-500">
                            {mode.value === 'leadlab' ? '+ RSE' : 'Standard'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Langue
                    </label>
                    <SessionLanguagePicker
                      compact
                      label=""
                      value={form.defaultLanguage}
                      onChange={(defaultLanguage) => setForm({ ...form, defaultLanguage })}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Heure
                    </label>
                    <input
                      type="time"
                      value={form.defaultTime}
                      onChange={(e) => setForm({ ...form, defaultTime: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 font-mono text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/10"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <CityPicker
                    compact
                    label="Ville / Lieu"
                    value={form.defaultLocation}
                    onChange={(defaultLocation, timezone) =>
                      // Choisir une ville connue présélectionne le fuseau du programme.
                      setForm((current) => ({
                        ...current,
                        defaultLocation,
                        ...(timezone ? { defaultTimezone: timezone } : {}),
                      }))
                    }
                  />
                </div>

                <div className="mt-5">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Fuseau horaire
                  </label>
                  <select
                    value={form.defaultTimezone}
                    onChange={(e) => setForm({ ...form, defaultTimezone: e.target.value })}
                    className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/10"
                  >
                    {!(SESSION_TIMEZONE_OPTIONS as readonly string[]).includes(form.defaultTimezone) && (
                      <option value={form.defaultTimezone}>
                        {timezoneShortLabel(form.defaultTimezone)}
                        {formatTimezoneOffset(form.defaultTimezone)
                          ? ` · ${formatTimezoneOffset(form.defaultTimezone)}`
                          : ''}
                      </option>
                    )}
                    {SESSION_TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz} value={tz}>
                        {timezoneShortLabel(tz)}
                        {formatTimezoneOffset(tz) ? ` · ${formatTimezoneOffset(tz)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-5">
                  <FacilitatorPicker
                    compact
                    label="Animateur"
                    value={form.defaultFacilitator}
                    onChange={(defaultFacilitator) => setForm({ ...form, defaultFacilitator })}
                  />
                </div>

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <div className="mb-4 flex items-end justify-between gap-3">
                    <div>
                      <div className="text-3xl font-black text-slate-900">
                        {scheduledCount}{' '}
                        <span className="text-base font-bold text-slate-500">séance{scheduledCount !== 1 ? 's' : ''}</span>
                      </div>
                      {dateRange && (
                        <p className="mt-1 text-xs font-medium text-slate-500">
                          {dateRange} · {timezoneShortLabel(form.defaultTimezone)}
                        </p>
                      )}
                    </div>
                    <span
                      className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] font-bold ${
                        form.gameMode === 'leadlab' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                      }`}
                    >
                      {gameModeLabel}
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={!canSaveProgram || saving || !form.name.trim() || scheduledCount === 0}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-4 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? 'Enregistrement...' : isEdit ? 'Enregistrer le programme' : 'Créer le programme'}
                    {!saving && <ArrowRight className="h-4 w-4" />}
                  </button>
                  <p className="mt-2.5 text-center text-xs font-medium text-slate-400">
                    {scheduledCount > 0
                      ? `${scheduledCount} session${scheduledCount !== 1 ? 's' : ''} seront générée${scheduledCount !== 1 ? 's' : ''}, une par séance.`
                      : 'Ajoutez au moins une séance pour continuer.'}
                  </p>
                </div>
                </div>

              {hasSlotSelected && (
                <button
                  type="button"
                  onClick={() => setSelectedSlotIndex(null)}
                  className="pointer-events-auto absolute inset-0 z-10 hidden flex-col items-center gap-3 pt-6 opacity-70 transition-opacity hover:opacity-100 xl:flex"
                  title="Fermer la séance — afficher la configuration"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-200/80 bg-white/90 text-indigo-600 shadow-sm">
                    <SlidersHorizontal className="h-4 w-4" />
                  </span>
                  <span
                    className="text-[9px] font-bold uppercase tracking-[0.2em] text-indigo-600"
                    style={{ writingMode: 'vertical-rl' }}
                  >
                    Config
                  </span>
                </button>
              )}
              </div>
            </aside>

            {/* Séances + édition — padding-left sur desktop, jamais de marge négative */}
            <div
              className={[
                'relative z-10 flex flex-col gap-4 transition-[padding] duration-500 ease-in-out',
                'xl:pointer-events-none xl:flex-row xl:gap-5',
                hasSlotSelected ? 'xl:pl-8' : 'xl:pl-[292px]',
              ].join(' ')}
            >
            <section
              className={[
                'pointer-events-auto min-w-0 flex-1 rounded-3xl border border-indigo-100 bg-white shadow-sm shadow-indigo-500/5 transition-all',
                hasSlotSelected ? 'p-4 xl:bg-white/95 xl:backdrop-blur-sm' : 'p-6',
              ].join(' ')}
            >
              <div className={`flex flex-wrap items-center justify-between gap-3 ${hasSlotSelected ? 'mb-3' : 'mb-5'}`}>
                <div>
                  <h2 className={`font-bold text-slate-900 ${hasSlotSelected ? 'text-lg' : 'text-xl'}`}>Séances</h2>
                  {!hasSlotSelected && (
                    <p className="mt-1 text-sm text-slate-500">
                      Cliquez un jour, ou ajoutez plusieurs dates d'un coup avec les outils ci-dessous.
                    </p>
                  )}
                </div>
                <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => setSessionsView('calendar')}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                      sessionsView === 'calendar' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-white'
                    }`}
                  >
                    <CalendarDays className="h-3.5 w-3.5" />
                    Calendrier
                  </button>
                  <button
                    type="button"
                    onClick={() => setSessionsView('list')}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                      sessionsView === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-white'
                    }`}
                  >
                    <List className="h-3.5 w-3.5" />
                    Liste
                  </button>
                </div>
              </div>

              <ProgramBulkTools
                mode={bulkMode}
                onModeChange={(mode) => {
                  setBulkMode(mode);
                  if (mode === 'multi') setSessionsView('calendar');
                  if (mode !== 'multi') setMultiSelectedDates([]);
                }}
                existingDates={existingDates}
                multiSelectedDates={multiSelectedDates}
                onClearMulti={() => setMultiSelectedDates([])}
                onAddDates={addManyDates}
                compact={hasSlotSelected}
              />

              {sessionsView === 'calendar' ? (
                <ProgramSessionsCalendar
                  key={`cal-${programId ?? 'new'}`}
                  slots={form.slots}
                  defaultSlotLabel={defaultSlotLabel}
                  defaultLanguage={form.defaultLanguage}
                  defaultTimezone={form.defaultTimezone}
                  defaultTime={form.defaultTime}
                  defaultLocation={form.defaultLocation}
                  defaultFacilitator={form.defaultFacilitator}
                  sessionCodes={sessionCodes}
                  selectedIndex={selectedSlotIndex}
                  onSelectIndex={setSelectedSlotIndex}
                  compact={hasSlotSelected}
                  onSlotsChange={(slots) => setForm({ ...form, slots })}
                  onDuplicate={duplicateSlot}
                  multiSelectMode={bulkMode === 'multi'}
                  multiSelectedDates={multiSelectedDates}
                  onToggleMultiDate={toggleMultiDate}
                  isException={isSlotException}
                />
              ) : (
                <ProgramSessionsList
                  slots={form.slots}
                  sessionCodes={sessionCodes}
                  selectedIndex={selectedSlotIndex}
                  compact={hasSlotSelected}
                  isException={isSlotException}
                  onRemove={removeSlot}
                  onSelect={(index) => {
                    setSelectedSlotIndex(index);
                  }}
                />
              )}

              <button
                type="button"
                onClick={addSeance}
                className={`mt-3 w-full rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 font-bold text-indigo-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 ${hasSlotSelected ? 'py-2.5 text-xs' : 'py-3.5 text-sm'}`}
              >
                + Ajouter une séance
              </button>
            </section>

            {/* Édition séance */}
            <aside
              className={[
                'pointer-events-auto hidden shrink-0 xl:sticky xl:top-24 xl:block xl:self-start',
                hasSlotSelected ? 'xl:w-[min(100%,440px)]' : 'xl:w-[300px]',
              ].join(' ')}
            >
              {hasSlotSelected ? (
                <ProgramSlotEditor
                  slot={selectedSlot}
                  index={selectedSlotIndex}
                  sessionCode={selectedSessionCode}
                  defaultSlotLabel={defaultSlotLabel}
                  canRemove
                  isException={isSlotException(selectedSlot)}
                  onChange={(patch) => updateSlot(selectedSlotIndex, patch)}
                  onDuplicate={() => duplicateSlot(selectedSlotIndex)}
                  onRemove={() => removeSlot(selectedSlotIndex)}
                  onClose={() => setSelectedSlotIndex(null)}
                />
              ) : (
                <ProgramSlotEditorPlaceholder />
              )}
            </aside>
            </div>
          </div>

          {hasSlotSelected && (
            <div className="mt-5 xl:hidden">
              <ProgramSlotEditor
                slot={selectedSlot}
                index={selectedSlotIndex!}
                sessionCode={selectedSessionCode}
                defaultSlotLabel={defaultSlotLabel}
                canRemove
                isException={isSlotException(selectedSlot)}
                onChange={(patch) => updateSlot(selectedSlotIndex, patch)}
                onDuplicate={() => duplicateSlot(selectedSlotIndex)}
                onRemove={() => removeSlot(selectedSlotIndex)}
                onClose={() => setSelectedSlotIndex(null)}
              />
            </div>
          )}
        </form>

        <AdminBuildStamp />
      </div>
    </div>
  );
};
